// src/layouts/services/index.js

// ==========================
// Imports
// ==========================
import { useEffect, useMemo, useState, useCallback } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Drawer from "@mui/material/Drawer";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Badge from "@mui/material/Badge";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import InputAdornment from "@mui/material/InputAdornment";
import Chip from "@mui/material/Chip";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAvatar from "components/MDAvatar";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDBadge from "components/MDBadge";
import DataTable from "examples/Tables/DataTable";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import PropTypes from "prop-types";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import { useLocation } from "react-router-dom";

import { supabase } from "../../../lib/supa";
import { Link } from "react-router-dom";

import ServiceStatusBadge from "components/ServiceStatusBadge";
import { MissingKeysMsg } from "components/MissingKeysMsg";
import { extractPlaceholders, applyTemplate, buildTemplateVars } from "../../../lib/buildtemplate";

// ==========================
// Helpers
// ==========================

const PAGE_SIZE = 20;

const unitToGr = (u) => {
  switch (String(u)) {
    case "days":
      return "ημέρες";
    case "weeks":
      return "εβδομάδες";
    case "months":
      return "μήνες";
    case "years":
      return "έτη";
    default:
      return String(u);
  }
};

const formatDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("el-GR");
  } catch {
    return String(d);
  }
};

const addDuration = (startISO, value, unit) => {
  if (!startISO || !value || !unit) return "";
  const d = new Date(startISO);
  if (Number.isNaN(d.getTime())) return "";
  const v = Number(value);
  if (unit === "days") d.setDate(d.getDate() + v);
  if (unit === "weeks") d.setDate(d.getDate() + v * 7);
  if (unit === "months") d.setMonth(d.getMonth() + v);
  if (unit === "years") d.setFullYear(d.getFullYear() + v);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const isUuid = (v) =>
  typeof v === "string" &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(v);

// ΝΕΟ: helper για αρχικά πελάτη
const getInitials = (full) => {
  if (!full) return "?";
  const parts = full.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] || "";
  const second = parts[1]?.[0] || "";
  return (first + second).toUpperCase();
};

const getServiceInitials = (name) => {
  const s = (name || "").trim();
  if (!s) return "?";
  const parts = s.split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] || "";
  const second = parts[1]?.[0] || "";
  return (first + second).toUpperCase();
};

// === Status mapping για 0/1/2 (numeric) ===
const STATUS_LABELS = {
  0: "ΕΝΕΡΓΗ",
  1: "ΛΗΓΕΙ ΣΥΝΤΟΜΑ",
  2: "ΛΗΓΜΕΝΗ",
};
const STATUS_COLORS = {
  0: "success",
  1: "warning",
  2: "error",
};

const dialogPaperSx = (theme) => ({
  backgroundColor:
    theme.palette.mode === "dark"
      ? theme.palette.background.default
      : theme.palette.background.paper,
  color: theme.palette.text.primary,
  borderRadius: 2,
});

// Προσθήκη: ελληνική μορφοποίηση διάρκειας (ενικός/πληθυντικός)
const formatDuration = (value, unit) => {
  const v = Number(value);
  if (!v || !unit) return "—";
  switch (String(unit)) {
    case "days":
      return v === 1 ? "1 ημέρα" : `${v} ημέρες`;
    case "weeks":
      return v === 1 ? "1 εβδομάδα" : `${v} εβδομάδες`;
    case "months":
      return v === 1 ? "1 μήνας" : `${v} μήνες`;
    case "years":
      return v === 1 ? "1 έτος" : `${v} έτη`;
    default:
      return `${v} ${unitToGr(unit)}`;
  }
};

// Helper: Κάλεσε προαιρετικά RPC και αγνόησε 42883 (function does not exist)
async function callOptionalRpc(fnName, args) {
  try {
    const { data, error } = await supabase.rpc(fnName, args);
    if (error) {
      // 42883: undefined_function (function does not exist)
      if (error.code === "42883" || /does not exist/i.test(error.message || "")) {
        console.warn(`[optional RPC] ${fnName}: missing on server, ignored.`);
        return null;
      }
      throw error;
    }
    return data ?? null;
  } catch (e) {
    console.warn(`[optional RPC] ${fnName} failed:`, e?.message || e);
    return null;
  }
}

// ==========================
// Component
// ==========================
export default function Services() {
  const [clientFilter, setClientFilter] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get("client");
    } catch {
      return null;
    }
  });

  const clearClientFilter = () => {
    setClientFilter(null);
    const url = new URL(window.location.href);
    url.searchParams.delete("client");
    window.history.replaceState(null, "", url.toString());
    setPage(0);
    // δεν καλούμε loadList εδώ — θα γίνει refetch από το useEffect λόγω deps
  };

  // ---------- List state ----------
  const [rows, setRows] = useState([]);
  const [raw, setRaw] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // server pagination & sorting
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [sortBy, setSortBy] = useState({ column: "created_at", ascending: false });

  // filters
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [serviceTypes, setServiceTypes] = useState([]);
  const [showEnabledOnly, setShowEnabledOnly] = useState(false); // ΝΕΟ φίλτρο Enabled

  // ---------- Drawer state ----------
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerTab, setDrawerTab] = useState(0); // 0 Service, 1 Reminders, 2 Ειδοποιήσεις
  const [selectedServiceId, setSelectedServiceId] = useState(null);
  // --- Add service states
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [clientsError, setClientsError] = useState(null);

  const clientLabel = (c) => {
    if (!c) return "";
    const name = `${c.first_name || ""} ${c.last_name || ""}`.trim();
    return c.company ? `${name} (${c.company})` : name || `#${c.id}`;
  };

  // Υπολογισμός ονόματος πελάτη για το φίλτρο χωρίς extra queries
  const clientFilterName = useMemo(() => {
    if (!clientFilter) return "";
    // 1) Από τα ήδη φορτωμένα rows (όλα είναι του ίδιου πελάτη όταν υπάρχει φίλτρο)
    if (raw?.length && raw[0]?.client_fullname) return raw[0].client_fullname;
    // 2) Από τη λίστα clients που έχει φορτωθεί ήδη
    const c = clients.find((x) => x.id === clientFilter);
    if (c) return clientLabel(c);
    // 3) Fallback σε id
    return `#${clientFilter}`;
  }, [clientFilter, raw, clients]);

  // Service form (edit/save)
  const [svcSaving, setSvcSaving] = useState(false);
  const [svcError, setSvcError] = useState(null);
  const [svcForm, setSvcForm] = useState({
    id: null,
    client_id: null,
    service_type_id: "",
    title: "",
    start_date: "",
    duration_value: 12,
    duration_unit: "months",
    price: "",
    notes: "",
    status: "active",
    enabled: true, // ΝΕΟ: default true
  });

  const openCreateDrawer = () => {
    setSvcError(null);
    setSvcSaving(false);
    setSelectedServiceId(null);

    // Καθάρισε ΟΛΑ τα reminders/preview/template states
    setReminders([]);
    setRemError(null);
    setAddOpen(false);
    setAddError(null);
    setEditingReminderId(null);
    setSelectedTemplateId("");
    setServiceMeta(null);

    setTemplates([]);
    setTmplError(null);
    setTmplLoading(false);

    setDrawerTab(0); // πήγαινε στο tab "Υπηρεσία"
    setSvcForm({
      id: null, // add mode
      client_id: null, // θα διαλέγεται από το autocomplete
      service_type_id: "",
      title: "",
      start_date: "",
      duration_value: 12,
      duration_unit: "months",
      price: "",
      notes: "",
      status: 0,
      enabled: true, // ΝΕΟ
    });
    setDrawerOpen(true);
  };

  // Reminders state (χωρίς send_time)
  const [notifByService, setNotifByService] = useState({});
  const [reminders, setReminders] = useState([]);
  const [remLoading, setRemLoading] = useState(false);
  const [remError, setRemError] = useState(null);

  const [addOpen, setAddOpen] = useState(false);
  const [addSaving, setAddSaving] = useState(false);
  const [addError, setAddError] = useState(null);
  const [editingReminderId, setEditingReminderId] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [tmplLoading, setTmplLoading] = useState(false);
  const [tmplError, setTmplError] = useState(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");

  const [addForm, setAddForm] = useState({
    relative_to: "end_date",
    direction: "before",
    offset_value: 15,
    offset_unit: "days",
    email_enabled: true,
    sms_enabled: false,
    active: true,
  });

  const [serviceMeta, setServiceMeta] = useState(null);

  // Notifications history state
  const [hist, setHist] = useState([]);
  const [histPage, setHistPage] = useState(0);
  const [histTotal, setHistTotal] = useState(0);
  const [histLoading, setHistLoading] = useState(false);
  const [histError, setHistError] = useState(null);

  // Delete dialogs
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [deleteReminderDialog, setDeleteReminderDialog] = useState({ open: false, id: null });

  // Dialogs & notifications for Renew
  const [renewDialog, setRenewDialog] = useState({ open: false, svc: null });
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  // ---------- Columns ----------
  const columns = useMemo(
    () => [
      { Header: "ΕΝΕΡΓΕΙΕΣ", accessor: "actions", width: "8%", align: "center" },
      { Header: "ΕΙΔΟΠΟΙΗΣΕΙΣ", accessor: "notifications", width: "12%" },
      { Header: "ΥΠΗΡΕΣΙΑ", accessor: "service_type", width: "14%", align: "center" },
      { Header: "ΠΕΡΙΓΡΑΦΗ", accessor: "title", width: "24%", Cell: TitleCell },
      { Header: "ΠΕΛΑΤΗΣ", accessor: "client_fullname", width: "18%" },
      { Header: "ΤΙΜΗ", accessor: "price", width: "10%" },
      { Header: "ΚΑΤΑΣΤΑΣΗ", accessor: "status", width: "12%" },
      { Header: "ΔΙΑΡΚΕΙΑ", accessor: "duration", width: "12%" },
      { Header: "ΛΗΞΗ", accessor: "end_date", width: "12%" },
      { Header: "ΣΗΜΕΙΩΣΕΙΣ", accessor: "notes", width: "6%", align: "center" }, // MOVED LAST
    ],
    []
  );
  // ---------- save service types ----------
  useEffect(() => {
    let on = true;
    (async () => {
      setClientsLoading(true);
      setClientsError(null);
      const { data, error } = await supabase
        .from("clients")
        .select("id,first_name,last_name,company")
        .order("last_name", { ascending: true })
        .limit(2000);
      if (!on) return;
      if (error) setClientsError(error.message || "Σφάλμα φόρτωσης πελατών");
      setClients(data || []);
      setClientsLoading(false);
    })();
    return () => {
      on = false;
    };
  }, []);

  // ---------- Load service types ----------
  useEffect(() => {
    let on = true;
    (async () => {
      const { data, error } = await supabase
        .from("service_types")
        .select("id,name,is_active")
        .eq("is_active", true)
        .order("name", { ascending: true });
      if (!on) return;
      if (error) return setServiceTypes([]);
      setServiceTypes(data || []);
    })();
    return () => {
      on = false;
    };
  }, []);

  // ---------- Load list ----------
  const loadList = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      // --- safe status parsing ---
      const isEmptyStatus =
        statusFilter === "" || statusFilter === null || typeof statusFilter === "undefined";
      const parsedStatus = isEmptyStatus ? null : Number(statusFilter);
      const allowed = new Set([0, 1, 2]);
      const hasStatus =
        !isEmptyStatus && Number.isInteger(parsedStatus) && allowed.has(parsedStatus);

      // -------- COUNT --------
      let cnt = supabase.from("v_services_list").select("*", { count: "exact", head: true });

      if (hasStatus) cnt = cnt.eq("status", parsedStatus); // <-- ήταν q αντί για cnt
      if (typeFilter) cnt = cnt.eq("service_type_id", typeFilter);
      if (clientFilter) cnt = cnt.eq("client_id", clientFilter);
      if (showEnabledOnly) cnt = cnt.eq("enabled", true); // ΝΕΟ: φίλτρο enabled

      if (search) {
        cnt = cnt.or(
          `title.ilike.%${search}%,client_fullname.ilike.%${search}%,service_type.ilike.%${search}%`
        );
      }

      const { count, error: cntErr } = await cnt;
      if (cntErr) throw cntErr;
      setTotal(count || 0);

      // -------- DATA --------
      let q = supabase.from("v_services_list").select(
        `
        id, client_id, client_fullname,
        service_type_id, service_type,
        title, notes,
        status, start_date, end_date,
        duration_value, duration_unit, price, created_at,
        days_left,
        enabled
      `
      );

      if (hasStatus) q = q.eq("status", parsedStatus);
      if (typeFilter) q = q.eq("service_type_id", typeFilter);
      if (clientFilter) q = q.eq("client_id", clientFilter);
      if (showEnabledOnly) q = q.eq("enabled", true); // ΝΕΟ: φίλτρο enabled
      if (search) {
        q = q.or(
          `title.ilike.%${search}%,client_fullname.ilike.%${search}%,service_type.ilike.%${search}%`
        );
      }

      // ασφάλεια στο sort column
      const sortCol = sortBy?.column || "created_at";
      const sortAsc = !!sortBy?.ascending;
      q = q.order(sortCol, { ascending: sortAsc, nullsFirst: false });

      const { data, error } = await q;
      if (error) throw error;

      setRaw(data || []);

      // preload reminders counts για τα badges
      const ids = (data || []).map((r) => r.id);
      await loadRemindersFor(ids);

      const mapped = (data || []).map((r) => mapServiceRow(r));
      setRows(mapped);
    } catch (e) {
      setLoadError(e.message || "Σφάλμα φόρτωσης");
      setRows([]);
      setRaw([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, sortBy, statusFilter, typeFilter, search, clientFilter, showEnabledOnly]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  // ---------- Rebuild rows when notif counts change ----------
  useEffect(() => {
    if (!raw.length) return;
    const notifVersion = Object.keys(notifByService).length;
    setRows(raw.map((r) => mapServiceRow(r, notifVersion)));
  }, [raw, notifByService]);

  // ---------- Notifications badges (counts) ----------
  const loadRemindersFor = async (serviceIds) => {
    if (!serviceIds?.length) {
      setNotifByService({});
      return;
    }
    const { data, error } = await supabase
      .from("service_reminders")
      .select(
        "service_id, active, email_enabled, sms_enabled, offset_value, offset_unit, direction, relative_to"
      )
      .in("service_id", serviceIds);

    if (error) {
      setNotifByService({});
      return;
    }
    const map = {};
    for (const r of data || []) {
      if (!r.active) continue;
      const sid = r.service_id;
      map[sid] ??= { email: { count: 0, details: [] }, sms: { count: 0, details: [] } };
      const label = `${r.direction === "before" ? "-" : "+"}${r.offset_value} ${unitToGr(
        r.offset_unit
      )} ${r.relative_to === "end_date" ? "από Λήξη" : "από Έναρξη"}`;
      if (r.email_enabled) {
        map[sid].email.count += 1;
        map[sid].email.details.push(label);
      }
      if (r.sms_enabled) {
        map[sid].sms.count += 1;
        map[sid].sms.details.push(label);
      }
    }
    setNotifByService(map);
  };

  const NotificationsCell = ({ serviceId }) => {
    const info = notifByService[serviceId] || {
      email: { count: 0, details: [] },
      sms: { count: 0, details: [] },
    };
    const emailTip = info.email.details.length
      ? info.email.details.join(" • ")
      : "Καμία email ειδοποίηση";
    const smsTip = info.sms.details.length ? info.sms.details.join(" • ") : "Καμία SMS ειδοποίηση";

    const openDrawer = () => {
      setSelectedServiceId(serviceId);
      setDrawerOpen(true);
      setDrawerTab(1); // πάμε κατευθείαν στα Reminders
    };

    return (
      <MDBox display="flex" alignItems="center" gap={1}>
        <Tooltip title={smsTip} arrow>
          <span>
            <IconButton
              size="small"
              onClick={openDrawer}
              sx={{ color: info.sms.count > 0 ? "info.main" : "text.disabled" }}
            >
              <Badge
                badgeContent={info.sms.count}
                color={info.sms.count > 0 ? "info" : "default"}
                showZero={false}
              >
                <Icon fontSize="small">sms</Icon>
              </Badge>
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={emailTip} arrow>
          <span>
            <IconButton
              size="small"
              onClick={openDrawer}
              sx={{ color: info.email.count > 0 ? "success.main" : "text.disabled" }}
            >
              <Badge
                badgeContent={info.email.count}
                color={info.email.count > 0 ? "success" : "default"}
                showZero={false}
              >
                <Icon fontSize="small">email</Icon>
              </Badge>
            </IconButton>
          </span>
        </Tooltip>
      </MDBox>
    );
  };
  NotificationsCell.propTypes = {
    serviceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  };

  // ---------- Toggle Enabled ----------
  const handleToggleEnabled = async (id, currentEnabled) => {
    if (!id) return;
    try {
      const { error } = await supabase
        .from("services")
        .update({ enabled: !currentEnabled })
        .eq("id", id);
      if (error) throw error;
      await loadList();
      setSnack({
        open: true,
        message: !currentEnabled ? "Η υπηρεσία ενεργοποιήθηκε." : "Η υπηρεσία απενεργοποιήθηκε.",
        severity: "success",
      });
    } catch (e) {
      setSnack({
        open: true,
        message: e.message || "Αποτυχία αλλαγής κατάστασης.",
        severity: "error",
      });
    }
  };

  // ---------- Actions ----------
  const renderActions = (r) => (
    <span>
      <Tooltip title="Επεξεργασία" arrow>
        <span>
          <IconButton
            size="small"
            color="info"
            onClick={() => {
              openServiceDrawer(r.id);
            }}
          >
            <Icon fontSize="small" color="info">
              edit
            </Icon>
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Διαγραφή" arrow>
        <span>
          <IconButton
            size="small"
            color="error"
            onClick={() => setDeleteDialog({ open: true, id: r.id })}
          >
            <Icon fontSize="small" color="error">
              delete
            </Icon>
          </IconButton>
        </span>
      </Tooltip>
      {/* ΝΕΟ: Ανανέωση υπηρεσίας */}
      <Tooltip title="Ανανέωση υπηρεσίας" arrow>
        <span>
          <IconButton
            size="small"
            color="success"
            onClick={() =>
              openRenewDialog({
                id: r.id,
                end_date: r.end_date,
                duration_value: r.duration_value,
                duration_unit: r.duration_unit,
                title: r.title,
              })
            }
          >
            <Icon fontSize="small">autorenew</Icon>
          </IconButton>
        </span>
      </Tooltip>
      {/* ΝΕΟ: Toggle Enabled */}
      <Tooltip title={r.enabled ? "Απενεργοποίηση υπηρεσίας" : "Ενεργοποίηση υπηρεσίας"} arrow>
        <span>
          <IconButton
            size="small"
            onClick={() => handleToggleEnabled(r.id, !!r.enabled)}
            color={r.enabled ? "info" : "error"}
          >
            <Icon fontSize="small">{r.enabled ? "visibility" : "visibility_off"}</Icon>
          </IconButton>
        </span>
      </Tooltip>
    </span>
  );

  // Άνοιγμα διαλόγου ανανέωσης
  const openRenewDialog = (svc) => {
    setRenewDialog({ open: true, svc });
  };

  // Επιβεβαίωση ανανέωσης
  const handleConfirmRenew = async () => {
    const s = renewDialog.svc;
    if (!s) return;
    const prevEnd = s.end_date; // κρατάμε το προηγούμενο end_date ως νέο start_date
    const newEnd = addDuration(prevEnd, Number(s.duration_value || 0), s.duration_unit);
    if (!newEnd) return;
    try {
      const { error } = await supabase
        .from("services")
        .update({ start_date: prevEnd, end_date: newEnd })
        .eq("id", s.id);
      if (error) throw error;

      // προαιρετικό: μην σκάει αν λείπει η RPC
      await callOptionalRpc("refresh_service_status_for", {
        p_service_id: s.id,
        p_days: 30,
        p_force: false,
      });

      await loadList();
      setSnack({ open: true, message: "Η υπηρεσία ανανεώθηκε.", severity: "success" });
    } catch (e) {
      setSnack({ open: true, message: e.message || "Αποτυχία ανανέωσης.", severity: "error" });
    } finally {
      setRenewDialog({ open: false, svc: null });
    }
  };

  // ---------- Row mapping helper (ενιαία λογική) ----------
  const mapServiceRow = (r, notifVersion = 0) => ({
    _id: r.id,
    service_type: r.service_type ? (
      <Tooltip title={r.service_type} arrow>
        <MDAvatar
          size="xs"
          bgColor="warning"
          sx={{ width: 28, height: 28, fontSize: "0.65rem", fontWeight: 600 }}
        >
          {getServiceInitials(r.service_type)}
        </MDAvatar>
      </Tooltip>
    ) : (
      "—"
    ),
    client_fullname: r.client_id ? (
      <MDBox
        component={Link}
        to={`/client-view/${r.client_id}`}
        display="inline-flex"
        alignItems="center"
        sx={{
          textDecoration: "none",
          "&:hover .client-name": { textDecoration: "underline" },
          "&:hover .client-avatar": { transform: "scale(1.1)" },
        }}
      >
        <MDAvatar
          className="client-avatar"
          size="xs"
          bgColor="info"
          sx={{
            width: 28,
            height: 28,
            fontSize: "0.65rem",
            mr: 1,
            transition: "transform 120ms ease",
            fontWeight: 600,
          }}
        >
          {getInitials(r.client_fullname || r.company)}
        </MDAvatar>
        <MDTypography className="client-name" variant="button" color="text">
          {r.client_fullname || r.company || "—"}
        </MDTypography>
      </MDBox>
    ) : (
      r.client_fullname || r.company || "—"
    ),
    title: r.title || "—",
    // Εμφάνισε icon μόνο αν υπάρχει μη κενό notes
    notes:
      r.notes && String(r.notes).trim() ? (
        <Tooltip title={String(r.notes).trim()} arrow>
          <Icon color="info" fontSize="small">
            sticky_note_2
          </Icon>
        </Tooltip>
      ) : null,
    status: <ServiceStatusBadge statusCode={r.status} daysLeft={r.days_left} />,
    start_date: formatDate(r.start_date),
    duration: formatDuration(r.duration_value, r.duration_unit),
    end_date: formatDate(r.end_date),
    price: typeof r.price === "number" ? r.price.toFixed(2) : "—",
    notifications: <NotificationsCell serviceId={r.id} key={`notif-${r.id}-${notifVersion}`} />,
    actions: renderActions(r),
  });

  // ---------- Drawer open helpers ----------
  const openServiceDrawer = async (serviceId) => {
    setSelectedServiceId(serviceId);
    setDrawerTab(0);
    await loadServiceForForm(serviceId);
    setDrawerOpen(true);
  };

  const loadServiceForForm = async (serviceId) => {
    // Διαβάζουμε από services για πλήρη στοιχεία
    const { data, error } = await supabase
      .from("services")
      .select(
        `
        id, client_id, service_type_id, title, status,
        start_date, end_date, duration_value, duration_unit, price, notes, enabled
      `
      )
      .eq("id", serviceId)
      .single();

    if (error || !data) {
      setSvcForm({
        id: serviceId,
        client_id: null,
        service_type_id: "",
        title: "",
        start_date: "",
        duration_value: 12,
        duration_unit: "months",
        price: "",
        notes: "",
        status: Number(data?.status ?? 0),
        enabled: true,
      });
      setSvcError(error?.message || "Σφάλμα ανάγνωσης υπηρεσίας");
      return;
    }

    setSvcError(null);
    setSvcForm({
      id: data.id,
      client_id: data.client_id,
      service_type_id: data.service_type_id || "",
      title: data.title || "",
      start_date: data.start_date || "",
      duration_value: data.duration_value || 12,
      duration_unit: data.duration_unit || "months",
      price: data.price ?? "",
      notes: data.notes ?? "",
      status: data.status || "active",
      enabled: !!data.enabled,
    });

    // meta για templates preview
    await loadServiceMeta(data.id);
    // refresh reminders tab when user switches
    await loadReminders(data.id);
    await loadTemplatesAll();
  };

  // ---------- Save service ----------
  const handleSaveService = async () => {
    setSvcError(null);
    setSvcSaving(true);
    try {
      const isNew = !svcForm.id; // flag για νέα υπηρεσία
      if (!svcForm.service_type_id) throw new Error("Διάλεξε τύπο υπηρεσίας.");
      if (!svcForm.start_date) throw new Error("Διάλεξε ημερομηνία έναρξης.");
      if (!svcForm.id && !svcForm.client_id) throw new Error("Διάλεξε πελάτη.");

      const end_date = addDuration(
        svcForm.start_date,
        svcForm.duration_value,
        svcForm.duration_unit
      );
      if (!end_date) throw new Error("Αποτυχία υπολογισμού λήξης.");

      const isUuid = (v) =>
        typeof v === "string" &&
        /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(v);

      if (!svcForm.id) {
        if (!isUuid(svcForm.client_id)) throw new Error("Διάλεξε πελάτη.");
      }
      if (!isUuid(svcForm.service_type_id)) throw new Error("Διάλεξε τύπο υπηρεσίας.");

      const payload = {
        service_type_id: svcForm.service_type_id,
        title: svcForm.title || null,
        start_date: svcForm.start_date,
        duration_value: Number(svcForm.duration_value) || 0,
        duration_unit: svcForm.duration_unit,
        end_date,
        price: svcForm.price !== "" ? Number(svcForm.price) : null,
        notes: svcForm.notes?.trim() ? svcForm.notes.trim() : null,
        enabled: !!svcForm.enabled, // ΝΕΟ
      };

      if (svcForm.id) {
        const { error } = await supabase.from("services").update(payload).eq("id", svcForm.id);
        if (error) throw error;

        // προαιρετικό: μην σκάει αν λείπει η RPC
        await callOptionalRpc("refresh_service_status_for", {
          p_service_id: svcForm.id,
          p_days: 30,
          p_force: false,
        });
      } else {
        const { data: inserted, error } = await supabase
          .from("services")
          .insert([{ ...payload, client_id: svcForm.client_id }])
          .select("id")
          .single();
        if (error) throw error;

        if (inserted?.id) {
          // προαιρετικό: μην σκάει αν λείπει η RPC
          await callOptionalRpc("refresh_service_status_for", {
            p_service_id: inserted.id,
            p_days: 30,
            p_force: false,
          });
        }
      }

      await loadList();

      // Κλείσε το drawer μόνο στη δημιουργία νέας υπηρεσίας (όχι στο edit)
      if (isNew) {
        setDrawerOpen(false);
      }
    } catch (e) {
      setSvcError(e.message || "Αποτυχία αποθήκευσης.");
    } finally {
      setSvcSaving(false);
    }
  };

  // ---------- Delete service ----------
  const handleDeleteService = async () => {
    if (!deleteDialog.id) return;
    try {
      await supabase.from("services").delete().eq("id", deleteDialog.id);
      await loadList();
    } finally {
      setDeleteDialog({ open: false, id: null });
      setDrawerOpen(false);
    }
  };

  // ---------- Reminders logic (reused from client-view) ----------
  async function loadServiceMeta(serviceId) {
    if (!serviceId) {
      setServiceMeta(null);
      return;
    }
    const { data, error } = await supabase
      .from("services")
      .select(
        `
        id, title, price, start_date, end_date,
        service_types ( id, name )
      `
      )
      .eq("id", serviceId)
      .single();
    if (error || !data) {
      setServiceMeta(null);
      return;
    }

    setServiceMeta({
      serviceTypeId: data?.service_types?.id || null,
      typeName: data?.service_types?.name || "",
      title: data?.title || "",
      start_date: data?.start_date || null,
      end_date: data?.end_date || null,
      price: typeof data?.price === "number" ? data.price : null,
    });
  }

  async function loadTemplatesAll() {
    setTmplLoading(true);
    setTmplError(null);
    const { data, error } = await supabase
      .from("notification_templates")
      .select("id,name,locale,subject,body_html,body_sms")
      .eq("is_active", true)
      .order("name", { ascending: true });
    if (error) setTmplError(error.message || "Σφάλμα φόρτωσης templates");
    setTemplates(data || []);
    setTmplLoading(false);
    setSelectedTemplateId("");
  }

  function getSelectedTemplate() {
    return templates.find((t) => t.id === selectedTemplateId) || null;
  }

  async function validateServiceForReminder(serviceId) {
    if (!serviceId) return { ok: false, data: null, error: "Άγνωστο service." };
    try {
      const { data, error } = await supabase
        .from("services")
        .select("id,start_date,end_date,status")
        .eq("id", serviceId)
        .single();
      if (error || !data) return { ok: false, data: null, error: "Το service δεν βρέθηκε." };
      return { ok: true, data };
    } catch (e) {
      return { ok: false, data: null, error: e.message || "Σφάλμα ελέγχου service." };
    }
  }

  // Load reminders (χωρίς send_time)
  async function loadReminders(serviceId) {
    if (!serviceId) {
      setReminders([]);
      return;
    }
    setRemLoading(true);
    setRemError(null);
    const { data, error } = await supabase
      .from("service_reminders")
      .select(
        "id,active,email_enabled,sms_enabled,offset_value,offset_unit,direction,relative_to,created_at,template_id"
      )
      .eq("service_id", serviceId)
      .order("created_at", { ascending: false });
    if (error) setRemError(error.message || "Σφάλμα φόρτωσης ειδοποιήσεων");
    setReminders(data || []);
    setRemLoading(false);
  }

  const handleToggleReminder = async (id, currentActive) => {
    if (!id) return;
    const newActive = !currentActive;
    const { error } = await supabase
      .from("service_reminders")
      .update({ active: newActive })
      .eq("id", id);
    if (!error) {
      await loadReminders(selectedServiceId);
      await loadRemindersFor(raw.map((r) => r.id));
    }
  };

  // Edit reminder form populate (χωρίς send_time)
  const handleEditReminder = (reminder) => {
    setAddForm({
      relative_to: reminder.relative_to,
      direction: reminder.direction,
      offset_value: reminder.offset_value,
      offset_unit: reminder.offset_unit,
      email_enabled: reminder.email_enabled,
      sms_enabled: reminder.sms_enabled,
      active: reminder.active,
    });
    setSelectedTemplateId(reminder.template_id || "");
    setEditingReminderId(reminder.id);
    setAddOpen(true);
  };

  const handleDeleteReminder = async () => {
    if (!deleteReminderDialog.id) return;
    try {
      await supabase.from("service_reminders").delete().eq("id", deleteReminderDialog.id);
      await loadReminders(selectedServiceId);
      await loadRemindersFor(raw.map((r) => r.id));
    } finally {
      setDeleteReminderDialog({ open: false, id: null });
    }
  };

  // Reset add form (χωρίς send_time)
  const handleCancelEditReminder = () => {
    setAddOpen(false);
    setEditingReminderId(null);
    setAddForm({
      relative_to: "end_date",
      direction: "before",
      offset_value: 15,
      offset_unit: "days",
      email_enabled: true,
      sms_enabled: false,
      active: true,
    });
    setSelectedTemplateId("");
  };

  // Save reminder payload (χωρίς send_time)
  const handleSaveReminder = async () => {
    if (!selectedServiceId) return;
    setAddError(null);

    if (!selectedTemplateId) {
      setAddError("Επίλεξε template, είναι υποχρεωτικό.");
      return;
    }
    if (addForm.offset_value === "" || Number.isNaN(Number(addForm.offset_value))) {
      setAddError("Δώσε αριθμητική απόσταση ειδοποίησης.");
      return;
    }
    if (Number(addForm.offset_value) < 0) {
      setAddError("Η απόσταση δεν μπορεί να είναι αρνητική.");
      return;
    }
    if (!addForm.email_enabled && !addForm.sms_enabled) {
      setAddError("Επίλεξε τουλάχιστον ένα κανάλι.");
      return;
    }
    if (!["days", "weeks", "months", "years"].includes(addForm.offset_unit)) {
      setAddError(`Μη υποστηριζόμενη μονάδα: ${addForm.offset_unit}`);
      return;
    }

    const svcCheck = await validateServiceForReminder(selectedServiceId);
    if (!svcCheck.ok) {
      setAddError(`Service validation failed: ${svcCheck.error}`);
      return;
    }
    const svcRow = svcCheck.data;
    if (addForm.relative_to === "end_date" && !svcRow.end_date) {
      setAddError("Το service δεν έχει end_date.");
      return;
    }
    if (addForm.relative_to === "start_date" && !svcRow.start_date) {
      setAddError("Το service δεν έχει start_date.");
      return;
    }

    setAddSaving(true);
    try {
      const payload = {
        relative_to: addForm.relative_to,
        direction: addForm.direction,
        offset_value: Number(addForm.offset_value),
        offset_unit: addForm.offset_unit,
        email_enabled: !!addForm.email_enabled,
        sms_enabled: !!addForm.sms_enabled,
        active: !!addForm.active,
        template_id: selectedTemplateId,
      };

      let error;
      if (editingReminderId) {
        const result = await supabase
          .from("service_reminders")
          .update(payload)
          .eq("id", editingReminderId);
        error = result.error;
      } else {
        const result = await supabase
          .from("service_reminders")
          .insert([{ ...payload, service_id: selectedServiceId }]);
        error = result.error;
      }

      if (error) {
        const msg = error.message || error.details || error.hint || "Αποτυχία αποθήκευσης";
        setAddError(`Database Error: ${msg}`);
        return;
      }

      await loadReminders(selectedServiceId);
      await loadRemindersFor(raw.map((r) => r.id));
      handleCancelEditReminder();
    } catch (e) {
      setAddError(e.message || "Απρόσμενο σφάλμα.");
    } finally {
      setAddSaving(false);
    }
  };

  // ---------- Notifications history ----------
  const loadHistory = useCallback(async (serviceId, pageIdx) => {
    if (!serviceId) return;
    setHistLoading(true);
    setHistError(null);
    try {
      // Χρησιμοποιούμε τον πίνακα service_reminder_logs
      let cnt = supabase
        .from("service_reminder_logs")
        .select("*", { count: "exact", head: true })
        .eq("service_id", serviceId);

      const { count, error: cErr } = await cnt;
      if (cErr) throw cErr;
      setHistTotal(count || 0);

      const from = pageIdx * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;

      const { data, error } = await supabase
        .from("service_reminder_logs")
        .select(
          "id,channel,status,sent_at,recipient_email,recipient_phone,provider_message_id,error_message,template_id"
        )
        .eq("service_id", serviceId)
        .order("sent_at", { ascending: false })
        .range(from, to);

      if (error) throw error;
      setHist(data || []);
    } catch (e) {
      setHistError(e.message || "Δεν είναι διαθέσιμο το ιστορικό.");
      setHist([]);
      setHistTotal(0);
    } finally {
      setHistLoading(false);
    }
  }, []);

  // ---------- Drawer effects ----------
  // Reminders & meta όταν είμαστε στο Tab 1
  useEffect(() => {
    if (!drawerOpen || !selectedServiceId) return;
    if (drawerTab !== 1) return;
    loadReminders(selectedServiceId);
    loadTemplatesAll();
    loadServiceMeta(selectedServiceId);
    // δεν χρειάζεται loadHistory εδώ
  }, [drawerOpen, selectedServiceId, drawerTab]);

  // History μόνο όταν είμαστε στο Tab 2 και αλλάζει η σελίδα
  useEffect(() => {
    if (!drawerOpen || !selectedServiceId) return;
    if (drawerTab !== 2) return;
    loadHistory(selectedServiceId, histPage);
  }, [drawerOpen, selectedServiceId, drawerTab, histPage, loadHistory]);

  // Template preview helpers
  const previewVars = useMemo(() => {
    return buildTemplateVars(serviceMeta, {
      offsetDays: Number(addForm.offset_value || 0),
      locale: "el-GR",
      currency: "EUR",
      // accountName,
      // clientFullname,
    });
  }, [serviceMeta, addForm.offset_value /*, accountName, clientFullname, currency, locale */]);

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === selectedTemplateId) || null,
    [templates, selectedTemplateId]
  );

  const previewSubject = useMemo(() => {
    if (!selectedTemplate?.subject) return "";
    return applyTemplate(selectedTemplate.subject, previewVars, { onMissing: "keep" });
  }, [selectedTemplate?.subject, previewVars]);

  const previewEmailHtml = useMemo(() => {
    if (!selectedTemplate?.body_html) return "";
    return applyTemplate(selectedTemplate.body_html, previewVars, { onMissing: "keep" });
  }, [selectedTemplate?.body_html, previewVars]);

  const previewSms = useMemo(() => {
    if (!selectedTemplate?.body_sms) return "";
    return applyTemplate(selectedTemplate.body_sms, previewVars, { onMissing: "keep" });
  }, [selectedTemplate?.body_sms, previewVars]);

  // Προαιρετικό: δείξε ποια placeholders δεν έχουν τιμή
  const getMissingKeysFor = (tpl, vars) => {
    const keys = extractPlaceholders(tpl || "");
    return keys.filter((k) => vars[k] == null || vars[k] === "");
  };

  const missingSubjectKeys = useMemo(
    () => getMissingKeysFor(selectedTemplate?.subject, previewVars),
    [selectedTemplate?.subject, previewVars]
  );

  const missingHtmlKeys = useMemo(
    () => getMissingKeysFor(selectedTemplate?.body_html, previewVars),
    [selectedTemplate?.body_html, previewVars]
  );

  const missingSmsKeys = useMemo(
    () => getMissingKeysFor(selectedTemplate?.body_sms, previewVars),
    [selectedTemplate?.body_sms, previewVars]
  );

  const commonTFProps = {
    fullWidth: true,
    size: "small",
    InputProps: { readOnly: true, spellCheck: false },
    // Χρησιμοποίησε FormHelperTextProps για να περάσει σωστά το style στο helperText
    FormHelperTextProps: { sx: { color: "text.secondary", mt: 0.5 } },
    // Ρύθμιση χρώματος του input κειμένου
    sx: { fontFamily: "monospace", whiteSpace: "pre-wrap" },
  };

  // ==========================
  // Render
  // ==========================
  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              {/* Header */}
              <MDBox
                mx={2}
                mt={-3}
                py={1}
                px={2}
                variant="gradient"
                bgColor="success"
                borderRadius="lg"
                coloredShadow="success"
              >
                <MDBox display="flex" alignItems="center" justifyContent="space-between">
                  <MDTypography variant="h6" color="white">
                    ΥΠΗΡΕΣΙΕΣ
                  </MDTypography>
                  {/* Προσθήκη υπηρεσίας */}
                  <Tooltip title="ΝΕΑ ΥΠΗΡΕΣΙΑ" arrow>
                    <MDAvatar
                      size="sm"
                      bgColor="dark"
                      sx={{ cursor: "pointer" }}
                      onClick={openCreateDrawer}
                    >
                      <Icon sx={{ color: "white" }}>add</Icon>
                    </MDAvatar>
                  </Tooltip>
                </MDBox>
              </MDBox>

              {/* Quick filters row */}
              <MDBox
                px={3}
                py={2}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gap={1}
                flexWrap="wrap"
              >
                {/* Left side: client filter chip (if any) + search */}
                <MDBox display="flex" alignItems="center" gap={1} sx={{ flex: 1, minWidth: 280 }}>
                  {clientFilter && (
                    <Chip
                      size="small"
                      color="info"
                      variant="filled"
                      label={clientFilterName}
                      onDelete={clearClientFilter}
                      deleteIcon={<Icon fontSize="small">close</Icon>}
                      sx={{
                        color: "white",
                        "& .MuiChip-deleteIcon": { color: "white" },
                      }}
                    />
                  )}
                  <MDInput
                    placeholder="Αναζήτηση σε Υπηρεσία, Πελάτη"
                    size="small"
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setPage(0);
                    }}
                    sx={{ minWidth: 260, flex: 1 }}
                    InputProps={{
                      endAdornment: search ? (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            aria-label="Καθαρισμός φίλτρου αναζήτησης"
                            onClick={() => {
                              setSearch("");
                              setPage(0);
                            }}
                          >
                            <Icon fontSize="small">close</Icon>
                          </IconButton>
                        </InputAdornment>
                      ) : null,
                    }}
                  />
                </MDBox>

                {/* Right side: status + type selects + ΝΕΟ enabled filter */}
                <MDBox display="flex" alignItems="center" gap={1}>
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel id="f-status">Κατάσταση</InputLabel>
                    <Select
                      labelId="f-status"
                      label="Κατάσταση"
                      value={statusFilter}
                      onChange={(e) => {
                        setStatusFilter(e.target.value);
                        setPage(0);
                      }}
                    >
                      <MenuItem value="">Όλες</MenuItem>
                      <MenuItem value={0}>Ενεργή</MenuItem>
                      <MenuItem value={1}>Λήγει σύντομα</MenuItem>
                      <MenuItem value={2}>Ληγμένη</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel id="f-type">Τύπος Υπηρεσίας</InputLabel>
                    <Select
                      labelId="f-type"
                      label="Τύπος Υπηρεσίας"
                      value={typeFilter}
                      onChange={(e) => {
                        setTypeFilter(e.target.value);
                        setPage(0);
                      }}
                    >
                      <MenuItem value="">Όλοι</MenuItem>
                      {serviceTypes.map((st) => (
                        <MenuItem key={st.id} value={st.id}>
                          {st.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* ΝΕΟ: Toggle φίλτρο για enabled */}
                  <FormControlLabel
                    control={
                      <Switch
                        checked={showEnabledOnly}
                        onChange={(e) => {
                          setShowEnabledOnly(e.target.checked);
                          setPage(0);
                        }}
                      />
                    }
                    label="Ενεργές μόνο"
                  />
                </MDBox>
              </MDBox>

              {/* Table */}
              <MDBox p={3}>
                {loadError && (
                  <MDTypography variant="button" color="error">
                    ΣΦΑΛΜΑ: {loadError}
                  </MDTypography>
                )}

                {loading ? (
                  <MDTypography variant="button" color="text">
                    ΦΟΡΤΩΣΗ…
                  </MDTypography>
                ) : (
                  <DataTable
                    table={{ columns, rows }}
                    isSorted={false}
                    entriesPerPage={{ defaultValue: 20, entries: [10, 20, 50, 100] }} // 20 ανά σελίδα (client-side)
                    showTotalEntries={true}
                    canSearch={false}
                    noEndBorder
                    // Βγάλε το external pagination, δεν το υποστηρίζει αυτό το DataTable
                    // pagination={{ page, pageSize: PAGE_SIZE, total, onPageChange: (p) => setPage(p) }}
                    onSortChange={(column, direction) => {
                      setSortBy({ column, ascending: direction === "asc" });
                    }}
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedServiceId(null);
          setReminders([]);
          setSelectedTemplateId("");
          setServiceMeta(null);
          setEditingReminderId(null);
          setAddOpen(false);
        }}
        PaperProps={{ sx: { width: { xs: "100%", sm: 560 } } }}
      >
        <MDBox p={3}>
          <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <MDTypography variant="h6">Υπηρεσία</MDTypography>
            <MDAvatar
              size="sm"
              bgColor="dark"
              sx={{ cursor: "pointer" }}
              onClick={() => setDrawerOpen(false)}
              title="Κλείσιμο"
            >
              <Icon sx={{ color: "white" }}>close</Icon>
            </MDAvatar>
          </MDBox>

          <Tabs
            value={drawerTab}
            onChange={(_, v) => setDrawerTab(v)}
            aria-label="service-tabs"
            sx={{ mb: 2 }}
          >
            <Tab label="Υπηρεσία" />
            <Tab label="Ειδοποιήσεις" />
            <Tab label="Ιστορικό" />
          </Tabs>

          {/* Tab 0: Service form */}
          {drawerTab === 0 && (
            <MDBox>
              {svcError && (
                <MDTypography variant="button" color="error" display="block" mb={1}>
                  {svcError}
                </MDTypography>
              )}
              {/* Add mode: autocomplete για επιλογή πελάτη */}
              {!svcForm.id && (
                <Grid item xs={12} sx={{ mb: 4 }}>
                  <Autocomplete
                    options={clients}
                    loading={clientsLoading}
                    value={clients.find((c) => c.id === svcForm.client_id) || null}
                    onChange={(_, val) => setSvcForm({ ...svcForm, client_id: val?.id || null })}
                    getOptionLabel={clientLabel}
                    isOptionEqualToValue={(opt, val) => opt.id === val.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="ΠΕΛΑΤΗΣ"
                        size="small"
                        error={!!clientsError}
                        helperText={clientsError ? "Δεν ήταν δυνατή η φόρτωσης πελατών" : ""}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {clientsLoading ? <CircularProgress size={16} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
              )}

              {/* Edit mode: read-only εμφάνιση πελάτη */}
              {svcForm.id && (
                <Grid item xs={12} sx={{ mb: 4 }}>
                  {" "}
                  {/* changed: added mb, removed invalid spacing prop */}
                  <MDInput
                    label="ΠΕΛΑΤΗΣ"
                    fullWidth
                    size="small"
                    value={clientLabel(clients.find((c) => c.id === svcForm.client_id)) || "—"}
                    InputProps={{ readOnly: true }}
                    sx={{
                      "& .MuiOutlinedInput-root": { background: "dark.main" },
                      "& input": { color: "dark.contrastText" },
                    }}
                  />
                </Grid>
              )}

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="svc-type-label">ΤΥΠΟΣ ΥΠΗΡΕΣΙΑΣ</InputLabel>
                    <Select
                      labelId="svc-type-label"
                      label="ΤΥΠΟΣ ΥΠΗΡΕΣΙΑΣ"
                      value={svcForm.service_type_id}
                      onChange={(e) => setSvcForm({ ...svcForm, service_type_id: e.target.value })}
                    >
                      {serviceTypes.length === 0 && (
                        <MenuItem disabled value="">
                          (Δεν υπάρχουν ενεργοί τύποι)
                        </MenuItem>
                      )}
                      {serviceTypes.map((st) => (
                        <MenuItem key={st.id} value={st.id}>
                          {st.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <MDInput
                    label="ΤΙΤΛΟΣ"
                    fullWidth
                    size="small"
                    value={svcForm.title}
                    onChange={(e) => setSvcForm({ ...svcForm, title: e.target.value })}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <MDInput
                    label="ΕΝΑΡΞΗ"
                    type="date"
                    fullWidth
                    size="small"
                    value={svcForm.start_date}
                    onChange={(e) => setSvcForm({ ...svcForm, start_date: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={6} sm={3}>
                  <MDInput
                    label="ΔΙΑΡΚΕΙΑ"
                    type="number"
                    fullWidth
                    size="small"
                    value={svcForm.duration_value}
                    onChange={(e) => setSvcForm({ ...svcForm, duration_value: e.target.value })}
                    inputProps={{ min: 1 }}
                  />
                </Grid>

                <Grid item xs={6} sm={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="du-label">ΜΟΝΑΔΑ</InputLabel>
                    <Select
                      labelId="du-label"
                      label="ΜΟΝΑΔΑ"
                      value={svcForm.duration_unit}
                      onChange={(e) => setSvcForm({ ...svcForm, duration_unit: e.target.value })}
                    >
                      <MenuItem value="days">Ημέρες</MenuItem>
                      <MenuItem value="weeks">Εβδομάδες</MenuItem>
                      <MenuItem value="months">Μήνες</MenuItem>
                      <MenuItem value="years">Έτη</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <MDInput
                    label="ΤΙΜΗ"
                    type="number"
                    fullWidth
                    size="small"
                    value={svcForm.price}
                    onChange={(e) => setSvcForm({ ...svcForm, price: e.target.value })}
                    inputProps={{ step: "1", min: "0" }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <MDInput
                    label="ΚΑΤΑΣΤΑΣΗ (αυτόματη)"
                    fullWidth
                    size="small"
                    value={STATUS_LABELS[Number(svcForm.status ?? 0)] ?? "—"}
                    InputProps={{ readOnly: true }}
                    sx={{
                      "& .MuiOutlinedInput-root": { background: "dark.main" },
                      "& input": { color: "dark.contrastText" },
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <MDInput
                    label="ΛΗΞΗ (preview)"
                    fullWidth
                    size="small"
                    value={
                      svcForm.start_date
                        ? addDuration(
                            svcForm.start_date,
                            Number(svcForm.duration_value || 0),
                            svcForm.duration_unit
                          ) || ""
                        : ""
                    }
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ readOnly: true }}
                    sx={{
                      "& .MuiOutlinedInput-root": { background: "dark.main" },
                      "& input": { color: "dark.contrastText" },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="ΣΗΜΕΙΩΣΕΙΣ"
                    fullWidth
                    size="small"
                    multiline
                    minRows={3}
                    value={svcForm.notes || ""} // NEW
                    onChange={(e) => setSvcForm({ ...svcForm, notes: e.target.value })} // NEW
                  />
                </Grid>

                {/* ΝΕΟ: Toggle enabled πεδίο */}
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!svcForm.enabled}
                        onChange={(e) => setSvcForm({ ...svcForm, enabled: e.target.checked })}
                      />
                    }
                    label="Ενεργή"
                  />
                </Grid>
              </Grid>

              <MDBox mt={3} display="flex" justifyContent="flex-end" gap={1}>
                <MDButton
                  variant="text"
                  color="secondary"
                  onClick={() => setDrawerOpen(false)}
                  disabled={svcSaving}
                >
                  ΑΚΥΡΩΣΗ
                </MDButton>
                <MDButton
                  variant="gradient"
                  color="info"
                  onClick={handleSaveService}
                  disabled={
                    svcSaving ||
                    (!svcForm.id && !isUuid(svcForm.client_id)) || // add-mode: ζητάμε έγκυρο client_id
                    !svcForm.service_type_id ||
                    !svcForm.start_date ||
                    !svcForm.title?.trim() ||
                    svcForm.price === "" ||
                    Number.isNaN(Number(svcForm.price))
                  }
                >
                  {svcSaving ? "ΑΠΟΘΗΚΕΥΣΗ..." : "ΑΠΟΘΗΚΕΥΣΗ"}
                </MDButton>
              </MDBox>
            </MDBox>
          )}

          {/* Tab 1: Reminders */}
          {drawerTab === 1 && (
            <MDBox>
              <MDTypography variant="button" color="text" mb={2} display="block">
                {serviceMeta
                  ? `${serviceMeta.typeName}${serviceMeta.title ? `: ${serviceMeta.title}` : ""}`
                  : "Φόρτωση..."}
              </MDTypography>

              <MDBox mb={2} display="flex" justifyContent="flex-end">
                <MDButton
                  size="small"
                  variant={addOpen ? "outlined" : "gradient"}
                  color="info"
                  onClick={() => {
                    if (addOpen) handleCancelEditReminder();
                    else setAddOpen(true);
                  }}
                >
                  {addOpen
                    ? "Άκυρο"
                    : editingReminderId
                    ? "Επεξεργασια ειδοποιησης"
                    : "Προσθηκη ειδοποιησης"}
                </MDButton>
              </MDBox>

              {addOpen && (
                <MDBox
                  p={2}
                  mb={2}
                  sx={{
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 2,
                    bgcolor: "background.default",
                  }}
                >
                  {addError && (
                    <MDTypography variant="button" color="error" display="block" mb={1}>
                      {addError}
                    </MDTypography>
                  )}
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel id="relto-label">ΣΧΕΤΙΚΟ ΜΕ</InputLabel>
                        <Select
                          labelId="relto-label"
                          label="ΣΧΕΤΙΚΟ ΜΕ"
                          value={addForm.relative_to}
                          onChange={(e) => setAddForm({ ...addForm, relative_to: e.target.value })}
                        >
                          <MenuItem value="start_date">Έναρξη</MenuItem>
                          <MenuItem value="end_date">Λήξη</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel id="direction-label">ΚΑΤΕΥΘΥΝΣΗ</InputLabel>
                        <Select
                          labelId="direction-label"
                          label="ΚΑΤΕΥΘΥΝΣΗ"
                          value={addForm.direction}
                          onChange={(e) => setAddForm({ ...addForm, direction: e.target.value })}
                        >
                          <MenuItem value="before">Πριν</MenuItem>
                          <MenuItem value="after">Μετά</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <MDInput
                        label="ΑΠΟΣΤΑΣΗ"
                        type="number"
                        fullWidth
                        size="small"
                        value={addForm.offset_value}
                        onChange={(e) => setAddForm({ ...addForm, offset_value: e.target.value })}
                        inputProps={{ min: 0 }}
                      />
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel id="unit-label">ΜΟΝΑΔΑ</InputLabel>
                        <Select
                          labelId="unit-label"
                          label="ΜΟΝΑΔΑ"
                          value={addForm.offset_unit}
                          onChange={(e) => setAddForm({ ...addForm, offset_unit: e.target.value })}
                        >
                          <MenuItem value="days">Ημέρες</MenuItem>
                          <MenuItem value="weeks">Εβδομάδες</MenuItem>
                          <MenuItem value="months">Μήνες</MenuItem>
                          <MenuItem value="years">Έτη</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4} display="flex" alignItems="center">
                      <MDInput
                        label="Προεπισκόπηση"
                        fullWidth
                        size="small"
                        value={`${addForm.direction === "before" ? "-" : "+"}${
                          addForm.offset_value
                        } ${unitToGr(addForm.offset_unit)} από ${
                          addForm.relative_to === "end_date" ? "Λήξη" : "Έναρξη"
                        }`}
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <MDBox
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        flexWrap="wrap"
                        gap={2}
                      >
                        <MDBox display="flex" alignItems="center" gap={3}>
                          <FormControlLabel
                            sx={{ m: 0 }}
                            control={
                              <Checkbox
                                checked={addForm.email_enabled}
                                onChange={(e) =>
                                  setAddForm({ ...addForm, email_enabled: e.target.checked })
                                }
                              />
                            }
                            label="Email"
                          />
                          <FormControlLabel
                            sx={{ m: 0 }}
                            control={
                              <Checkbox
                                checked={addForm.sms_enabled}
                                onChange={(e) =>
                                  setAddForm({ ...addForm, sms_enabled: e.target.checked })
                                }
                              />
                            }
                            label="SMS"
                          />
                        </MDBox>

                        <FormControlLabel
                          sx={{ m: 0 }}
                          control={
                            <Switch
                              checked={addForm.active}
                              onChange={(e) => setAddForm({ ...addForm, active: e.target.checked })}
                            />
                          }
                          label="Ενεργή"
                        />
                      </MDBox>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel id="tmpl-label">Template</InputLabel>
                        <Select
                          labelId="tmpl-label"
                          label="Template"
                          value={selectedTemplateId}
                          onChange={(e) => setSelectedTemplateId(e.target.value)}
                        >
                          {tmplLoading && <MenuItem disabled>(Φόρτωση…)</MenuItem>}
                          {tmplError && <MenuItem disabled>Σφάλμα: {tmplError}</MenuItem>}
                          {!tmplLoading && templates.length === 0 && (
                            <MenuItem disabled>(Δεν υπάρχουν templates)</MenuItem>
                          )}
                          {templates.map((t) => (
                            <MenuItem key={t.id} value={t.id}>
                              {t.name} {t.locale ? `(${t.locale})` : ""}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    {selectedTemplateId && (
                      <Grid item xs={12}>
                        <TextField
                          label="Προεπισκόπηση Subject (Email)"
                          value={previewSubject}
                          placeholder="—"
                          {...commonTFProps}
                        />
                        {MissingKeysMsg(missingSubjectKeys)}
                      </Grid>
                    )}

                    {selectedTemplateId && (
                      <Grid item xs={12}>
                        <TextField
                          label="Προεπισκόπηση Email (HTML)"
                          value={previewEmailHtml}
                          placeholder="—"
                          multiline
                          minRows={3}
                          {...commonTFProps}
                        />
                        {MissingKeysMsg(missingHtmlKeys)}
                      </Grid>
                    )}

                    {selectedTemplateId && (
                      <Grid item xs={12}>
                        <TextField
                          label="Προεπισκόπηση SMS (Plain Text)"
                          value={previewSms}
                          placeholder="—"
                          multiline
                          minRows={2}
                          {...commonTFProps}
                        />
                        {MissingKeysMsg(missingSmsKeys)}
                      </Grid>
                    )}
                  </Grid>

                  <MDBox mt={2} display="flex" justifyContent="flex-end" gap={1}>
                    <MDButton
                      variant="text"
                      color="secondary"
                      onClick={handleCancelEditReminder}
                      disabled={addSaving}
                    >
                      Άκυρο
                    </MDButton>
                    <MDButton
                      variant="gradient"
                      color="info"
                      onClick={handleSaveReminder}
                      disabled={addSaving}
                    >
                      {addSaving ? "Αποθήκευση..." : "Αποθήκευση"}
                    </MDButton>
                  </MDBox>
                </MDBox>
              )}

              {remError && (
                <MDTypography variant="button" color="error" display="block">
                  Σφάλμα: {remError}
                </MDTypography>
              )}

              {remLoading ? (
                <MDTypography variant="button" color="text">
                  Φόρτωση…
                </MDTypography>
              ) : reminders.length === 0 ? (
                <MDTypography variant="button" color="text">
                  Δεν υπάρχουν ειδοποιήσεις.
                </MDTypography>
              ) : (
                reminders.map((r) => {
                  const tip = `${r.direction === "before" ? "-" : "+"}${r.offset_value} ${unitToGr(
                    r.offset_unit
                  )} από ${r.relative_to === "end_date" ? "Λήξη" : "Έναρξη"}`;
                  return (
                    <MDBox
                      key={r.id}
                      px={2}
                      py={1.5}
                      mb={1}
                      sx={{ border: 1, borderColor: "divider", borderRadius: 2 }}
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      gap={2}
                    >
                      <MDBox display="flex" flexDirection="column">
                        <MDTypography variant="button">{tip}</MDTypography>
                        <MDBox display="flex" gap={1} mt={0.5}>
                          {r.email_enabled && (
                            <MDBadge
                              badgeContent="Email"
                              color="success"
                              size="sm"
                              variant="gradient"
                            />
                          )}
                          {r.sms_enabled && (
                            <MDBadge badgeContent="SMS" color="info" size="sm" variant="gradient" />
                          )}
                          <MDBadge
                            badgeContent={r.active ? "ΕΝΕΡΓΗ" : "ΑΝΕΝΕΡΓΗ"}
                            color={r.active ? "success" : "secondary"}
                            size="sm"
                            variant="gradient"
                          />
                        </MDBox>
                      </MDBox>

                      <MDBox display="flex" alignItems="center" gap={1}>
                        <Tooltip title="Επεξεργασία">
                          <span>
                            <IconButton
                              size="small"
                              color="info"
                              onClick={() => handleEditReminder(r)}
                            >
                              <Icon fontSize="small">edit</Icon>
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Διαγραφή">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setDeleteReminderDialog({ open: true, id: r.id })}
                            >
                              <Icon fontSize="small">delete</Icon>
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={r.active ? "Απενεργοποίηση" : "Ενεργοποίηση"}>
                          <span>
                            <IconButton
                              size="small"
                              color={r.active ? "info" : "warning"}
                              onClick={() => handleToggleReminder(r.id, r.active)}
                            >
                              <Icon fontSize="small">
                                {r.active ? "visibility" : "visibility_off"}
                              </Icon>
                            </IconButton>
                          </span>
                        </Tooltip>
                      </MDBox>
                    </MDBox>
                  );
                })
              )}

              {/* Delete reminder dialog */}
              <Dialog
                open={deleteReminderDialog.open}
                onClose={() => setDeleteReminderDialog({ open: false, id: null })}
                PaperProps={{ sx: dialogPaperSx }}
              >
                <DialogTitle sx={{ bgcolor: "dark.main", color: "dark.contrastText" }}>
                  Επιβεβαίωση διαγραφής
                </DialogTitle>
                <DialogContent sx={{ bgcolor: "background.default", color: "text.primary" }}>
                  <MDTypography>Θέλεις σίγουρα να διαγράψεις αυτή την ειδοποίηση;</MDTypography>
                </DialogContent>
                <DialogActions sx={{ bgcolor: "background.default" }}>
                  <MDButton
                    onClick={() => setDeleteReminderDialog({ open: false, id: null })}
                    color="secondary"
                  >
                    Άκυρο
                  </MDButton>
                  <MDButton onClick={handleDeleteReminder} color="error" variant="contained">
                    Διαγραφή
                  </MDButton>{" "}
                </DialogActions>
              </Dialog>
            </MDBox>
          )}

          {/* Tab 2: Notifications history */}
          {drawerTab === 2 && (
            <MDBox>
              {histError ? (
                <MDTypography variant="button" color="error">
                  {histError}
                </MDTypography>
              ) : histLoading ? (
                <MDTypography variant="button" color="text">
                  Φόρτωση…
                </MDTypography>
              ) : hist.length === 0 ? (
                <MDTypography variant="button" color="text">
                  Δεν υπάρχει ιστορικό ή δεν έχει υλοποιηθεί ακόμα ο πίνακας.
                </MDTypography>
              ) : (
                hist.map((h) => (
                  <MDBox
                    key={h.id}
                    px={2}
                    py={1.5}
                    mb={1}
                    sx={{ border: 1, borderColor: "divider", borderRadius: 2 }}
                  >
                    <MDBox
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={0.5}
                    >
                      <MDTypography variant="button">
                        {h.channel?.toUpperCase() || "—"} • {h.status || "—"}
                      </MDTypography>
                      <MDTypography variant="caption" color="text">
                        {formatDate(h.sent_at)}
                      </MDTypography>
                    </MDBox>
                    <MDTypography variant="button" color="text">
                      {h.recipient_email || h.recipient_phone
                        ? `→ ${h.recipient_email || h.recipient_phone}`
                        : "—"}
                    </MDTypography>
                    {h.error_message && (
                      <MDTypography variant="caption" color="error" display="block">
                        {h.error_message}
                      </MDTypography>
                    )}
                  </MDBox>
                ))
              )}

              {/* Simple pager for history */}
              {histTotal > PAGE_SIZE && (
                <MDBox mt={2} display="flex" justifyContent="space-between" alignItems="center">
                  <MDTypography variant="caption" color="text">
                    {Math.min(histPage * PAGE_SIZE + 1, histTotal)} -{" "}
                    {Math.min((histPage + 1) * PAGE_SIZE, histTotal)} από {histTotal}
                  </MDTypography>
                  <MDBox display="flex" gap={1}>
                    <MDButton
                      variant="outlined"
                      size="small"
                      disabled={histPage === 0}
                      onClick={() => setHistPage((p) => Math.max(0, p - 1))}
                    >
                      Προηγούμενη
                    </MDButton>
                    <MDButton
                      variant="outlined"
                      size="small"
                      disabled={(histPage + 1) * PAGE_SIZE >= histTotal}
                      onClick={() => setHistPage((p) => p + 1)}
                    >
                      Επόμενη
                    </MDButton>
                  </MDBox>
                </MDBox>
              )}
            </MDBox>
          )}
        </MDBox>
      </Drawer>

      {/* Delete service dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, id: null })}
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle sx={{ bgcolor: "dark.main", color: "dark.contrastText" }}>
          Επιβεβαίωση διαγραφής
        </DialogTitle>
        <DialogContent sx={{ bgcolor: "background.default", color: "text.primary" }}>
          <MDTypography>Θέλεις σίγουρα να διαγράψεις αυτή την υπηρεσία;</MDTypography>
        </DialogContent>
        <DialogActions sx={{ bgcolor: "background.default" }}>
          <MDButton onClick={() => setDeleteDialog({ open: false, id: null })} color="secondary">
            Άκυρο
          </MDButton>
          <MDButton onClick={handleDeleteService} color="error" variant="contained">
            Διαγραφή
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* ΝΕΟ: Renew service dialog */}
      <Dialog
        open={renewDialog.open}
        onClose={() => setRenewDialog({ open: false, svc: null })}
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle sx={{ bgcolor: "dark.main", color: "dark.contrastText" }}>
          Ανανέωση υπηρεσίας
        </DialogTitle>
        <DialogContent sx={{ bgcolor: "background.default", color: "text.primary" }}>
          <MDTypography variant="button" display="block">
            Τρέχουσα λήξη: {formatDate(renewDialog.svc?.end_date)}
          </MDTypography>
          <MDTypography variant="button" display="block" sx={{ mt: 1 }}>
            Διάρκεια: +{renewDialog.svc?.duration_value} {unitToGr(renewDialog.svc?.duration_unit)}
          </MDTypography>
          <MDTypography variant="button" display="block" sx={{ mt: 1 }}>
            Νέα λήξη (προεπισκόπηση):{" "}
            {renewDialog.svc
              ? formatDate(
                  addDuration(
                    renewDialog.svc.end_date,
                    Number(renewDialog.svc.duration_value || 0),
                    renewDialog.svc.duration_unit
                  )
                )
              : "—"}
          </MDTypography>
        </DialogContent>
        <DialogActions sx={{ bgcolor: "background.default" }}>
          <MDButton onClick={() => setRenewDialog({ open: false, svc: null })} color="secondary">
            Άκυρο
          </MDButton>
          <MDButton
            onClick={handleConfirmRenew}
            color="success"
            variant="contained"
            disabled={(() => {
              const s = renewDialog.svc;
              if (!s) return true;
              const allowed = new Set(["days", "weeks", "months", "years"]);
              if (!s.end_date) return true;
              if (!s.duration_value || Number(s.duration_value) <= 0) return true;
              if (!allowed.has(s.duration_unit)) return true;
              return false;
            })()}
          >
            ΟΚ
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Snackbar ενημέρωσης */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity}
          sx={{ width: "100%" }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

// Προσθήκη: σύντμηση κειμένου
const ellipsize = (s, max = 20) => (s && s.length > max ? `${s.slice(0, max)}…` : s || "—");

// Κελί για την ΠΕΡΙΓΡΑΦΗ με tooltip πλήρους κειμένου
const TitleCell = ({ value }) => {
  const full = typeof value === "string" ? value : value == null ? "" : String(value);
  const short = ellipsize(full, 20);
  return (
    <Tooltip title={full || "—"} arrow>
      <span>{short}</span>
    </Tooltip>
  );
};
TitleCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
